"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import type { AppModel } from "@jti/user-profile";

import { buildReferralPersonalCodeInputs } from "@/components/ReferralPersonalCode";
import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES } from "@/libs/builder/consts";
import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";

import { loyaltyReferralCodeExtendedInputs } from "../LoyaltyReferralCodeExtended/LoyaltyReferralCodeExtended.builder";

const UserProfile = dynamic(async () => (await import("./UserProfile")).UserProfile, {
  ssr: false,
});

export const userProfileInputs = [
  {
    name: "payOnDelivery",
    type: "object",
    defaultValue: {
      paymentMethod:
        "You have chosen payment on delivery, please make sure to follow up with delivery and have the exact amount when you receive the product.",
    },
    subFields: [
      {
        name: "paymentMethod",
        type: "richText",
      },
    ],
  },
  {
    name: "ageVerificationMethodImages",
    type: "reference",
    model: getBuilderModelName("age-verification-method-images"),
  },
  {
    name: "zeroValueOrder",
    type: "object",
    defaultValue: {
      userNotification: "This order is free of charge. No payment required.",
    },
    subFields: [
      {
        name: "userNotification",
        type: "richText",
      },
    ],
  },
  {
    name: "headerBanner",
    type: "object",
    defaultValue: {
      hideGreetingMessage: false,
    },
    subFields: [
      {
        name: "hideGreetingMessage",
        type: "boolean",
      },
    ],
  },
  {
    name: "failedPaymentFlow",
    type: "object",
    defaultValue: {
      title: "Your order is pending payment",
      message: "Please proceed to payment to complete your order.",
      submitButtonLabel: "Proceed to payment",
    },
    subFields: [
      {
        name: "title",
        type: "string",
      },
      {
        name: "message",
        type: "richText",
        friendlyName: "Failed payment notification message",
      },
      {
        name: "submitButtonLabel",
        type: "string",
        friendlyName: "Proceed to payment button label",
        required: true,
      },
    ],
  },
  {
    name: "communicationPreferences",
    type: "object",
    subFields: [
      {
        name: "isLanguagePreferenceHidden",
        type: "boolean",
      },
      {
        name: "purposesGroupingType",
        type: "string",
        enum: ["category", "section"],
        required: true,
      },
      {
        name: "successUpdateMessage",
        type: "richText",
      },
      {
        name: "oneTrustErrorMessage",
        type: "richText",
      },
      {
        name: "oneTrustErrorLinkText",
        type: "string",
      },
      {
        name: "oneTrustErrorRedirectUrl",
        type: "url",
        required: true,
      },
      {
        name: "emailFieldDescription",
        type: "string",
      },
      {
        name: "phoneFieldDescription",
        type: "string",
      },
      {
        name: "surveyFieldDescription",
        type: "string",
      },
      {
        name: "postFieldDescription",
        type: "string",
      },
      {
        name: "gdprFieldDescription",
        type: "string",
      },
      {
        name: "isEmailChannelDisabled",
        type: "boolean",
        required: true,
        defaultValue: false,
      },
      {
        name: "isPhoneChannelDisabled",
        type: "boolean",
        required: true,
        defaultValue: false,
      },
      {
        name: "isPostChannelDisabled",
        type: "boolean",
        required: true,
        defaultValue: false,
      },
      {
        name: "isOptionalConsentNotRequired",
        type: "boolean",
        required: true,
        defaultValue: false,
      },
      {
        name: "consentOptionsListType",
        type: "string",
        enum: ["default", "extended"],
      },
      {
        name: "isColumnDisplayOfConsents",
        friendlyName: "Display style of consents (row/column)",
        type: "boolean",
        defaultValue: false,
      },
    ],
    defaultValue: {
      purposesGroupingType: "category",
      isColumnDisplayOfConsents: false,
    },
  },
  {
    name: "genderInformation",
    type: "object",
    subFields: [
      {
        name: "isGenderEnabled",
        type: "boolean",
        friendlyName: "Should gender info be displayed and editable?",
      },
    ],
    defaultValue: { isGenderEnabled: false },
  },
  {
    name: "deviceManagement",
    type: "object",
    defaultValue: {
      isLastDeviceRemovalRestricted: false,
      deviceCodeLength: "short",
      successfulDeviceRegistrationCTA: "You have successfully registered your device.",
    },
    subFields: [
      {
        name: "isLastDeviceRemovalRestricted",
        type: "boolean",
      },
      {
        name: "deviceCodeLength",
        type: "object",
        subFields: [
          {
            name: "short",
            type: "boolean",
            friendlyName: "Short code",
            defaultValue: true,
          },
          {
            name: "long",
            type: "boolean",
            friendlyName: "Long code",
            defaultValue: false,
          },
          {
            name: "gen4",
            type: "boolean",
            friendlyName: "Gen 4 code",
            defaultValue: false,
          },
        ],
      },
      {
        name: "successfulDeviceRegistrationCTA",
        type: "richText",
      },
      {
        name: "desktopVideoTutorialUrl",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        friendlyName: "Desktop video tutorial",
      },
      {
        name: "mobileVideoTutorialUrl",
        type: "file",
        friendlyName: "Mobile video tutorial",
        allowedFileTypes: VIDEO_FILE_TYPES,
      },
      {
        name: "posterTutorialUrl",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "isVideoTutorialSectionHidden",
        friendlyName: "Hide video tutorial section",
        type: "boolean",
      },
    ],
  },
  {
    name: "sharableProfile",
    type: "object",
    subFields: [
      {
        name: "isSharableProfileEnabled",
        friendlyName: "Is sharing profile via QR code enabled?",
        type: "boolean",
      },
      {
        name: "isSharableProfileLoyaltyEnabled",
        type: "boolean",
        required: true,
      },
      {
        name: "clubCardLogo",
        friendlyName: "Club Card logo",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
    ],
  },
  {
    name: "orderList",
    type: "object",
    subFields: [
      {
        name: "isOfflineOrderEnabled",
        type: "boolean",
      },
    ],
    defaultValue: { isOfflineOrderEnabled: false },
  },
  {
    name: "personalInformation",
    type: "object",
    subFields: [
      {
        name: "isDobChangeRestricted",
        type: "boolean",
      },
      {
        name: "dobChangeRestrictedMessage",
        type: "string",
      },
      {
        name: "hideMyAddress",
        type: "boolean",
        defaultValue: false,
      },
    ],
  },
  {
    name: "passPersonalDataUpdate",
    type: "object",
    defaultValue: {
      isPassPersonalDataUpdate: false,
      title: "Age Verification",
      logoSrc: "",
      logoAlt: "logo alt",
      description: "By continuing, you agree to our Terms of Service and Privacy Policy.",
      continueButtonLabel: "Continue",
      tryAgainButtonLabel: "Try again",
      backButtonLabel: "Back",
      legalNotice:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqu",
      desktopImageSrc: "",
      tabletImageSrc: "",
      mobileImageSrc: "",
      verificationTitle: "Verifying your phone number",
      alt: "alt",
      verificationSteps: "To access this site, please verify your identity and phone number using PASS AV",
      failedProviderVerificationError: "Failed to verify your phone number. Please try again.",
      waitingMessage: "We are verifying your phone number. This may take a few seconds.",
      helperText:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqu",
      verificationErrorHelperText: "verification error helper text",
      missingDataFromAvError: "missing data from av error",
    },
    subFields: [
      {
        name: "isPassPersonalDataUpdate",
        type: "boolean",
      },
      {
        name: "logoSrc",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "logoAlt",
        type: "string",
      },
      {
        name: "title",
        type: "string",
      },
      {
        name: "description",
        type: "richText",
        required: false,
      },
      {
        name: "continueButtonLabel",
        type: "string",
        required: false,
      },
      {
        name: "tryAgainButtonLabel",
        type: "string",
        required: false,
      },
      {
        name: "backButtonLabel",
        type: "string",
        required: false,
      },
      {
        name: "legalNotice",
        type: "richText",
        required: false,
      },
      {
        name: "desktopImageSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "tabletImageSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "mobileImageSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "alt",
        type: "string",
        required: false,
      },
      {
        name: "verificationTitle",
        type: "string",
        required: false,
      },
      {
        name: "verificationSteps",
        type: "richText",
        required: false,
      },
      {
        name: "failedProviderVerificationError",
        type: "string",
        required: false,
      },
      {
        name: "waitingMessage",
        type: "richText",
      },
      {
        name: "helperText",
        type: "string",
        required: false,
      },
      {
        name: "verificationErrorHelperText",
        type: "string",
        required: false,
      },
      {
        name: "missingDataFromAvError",
        type: "string",
        required: false,
      },
    ],
  },
  {
    name: "subscriptionConfiguration",
    type: "object",
    defaultValue: {
      title: "Subscription config title",
    },
    subFields: [
      {
        name: "logoSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "bannerBackgroundImage",
        friendlyName: "Banner background image",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "title",
        type: "string",
        required: false,
      },
      {
        name: "priceDisclaimerEnabled",
        type: "boolean",
        friendlyName: "Is price disclaimer enabled?",
        required: false,
      },
      {
        name: "subscribeNowUrl",
        type: "string",
        required: false,
      },
      {
        name: "cancellationReasons",
        type: "list",
        friendlyName: "Subscription cancellation reasons",
        required: false,
        defaultValue: [],
        subFields: [
          {
            name: "label",
            type: "string",
            required: true,
          },
          {
            name: "showCustomReasonInput",
            type: "boolean",
            friendlyName: "Show custom reason input",
            required: false,
          },
        ],
      },
    ],
  },
  {
    name: "referralPersonalCodeConfiguration",
    type: "object",
    defaultValue: {
      isHidden: true,
      variant: "default",
      header: "",
      smallText: "",
      backgroundImage: "",
      backgroundImageTablet: "",
      backgroundImageMobile: "",
      referralPageVariant: false,
      topHeader: "",
      codeTitle: "",
      shareTitle: "",
      description: "",
      withBottomCta: false,
      bottomCtaLabel: "",
      bottomCtaLink: "",
    },
    subFields: [
      {
        name: "isHidden",
        friendlyName: "Hide Referral Personal Code Block",
        type: "boolean",
      },
      ...buildReferralPersonalCodeInputs(),
    ],
  },
  {
    name: "deleteAccount",
    type: "object",
    defaultValue: {
      header: "Delete Account",
      checkboxContent: "I understand that deleting my account will remove all my data and preferences.",
      content: "Are you sure you want to delete your account? This action cannot be undone.",
      successCallbackContent: "Your account has been successfully deleted. We are sorry to see you go.",
      errorCallbackContent: "There was an error deleting your account. Please try again later.",
      deletionType: "optionsDeletion",
      deactivationRadioButtonText: "Deactivate my account",
      cleanDeletionRadioButtonText: "Delete my account permanently",
      successfulDisablingAccountMessage: "Your account has been successfully deactivated.",
      disableFailedErrorMessage: "There was an error deactivating your account. Please try again later.",
    } satisfies AppModel["deleteAccount"],
    subFields: [
      {
        name: "header",
        type: "string",
        required: true,
      },
      {
        name: "checkboxContent",
        type: "richText",
        required: true,
      },
      {
        name: "content",
        type: "richText",
        required: true,
      },
      {
        name: "successCallbackContent",
        type: "richText",
        required: true,
      },
      {
        name: "errorCallbackContent",
        type: "richText",
        required: true,
      },
      {
        name: "deletionType",
        type: "string",
        enum: ["optionsDeletion", "cleanDeletion"],
        required: true,
      },
      {
        name: "deactivationRadioButtonText",
        type: "string",
        required: true,
      },
      {
        name: "cleanDeletionRadioButtonText",
        type: "string",
        required: true,
      },
      {
        name: "successfulDisablingAccountMessage",
        type: "string",
        required: true,
      },
      {
        name: "disableFailedErrorMessage",
        type: "string",
        required: true,
      },
      {
        name: "logoImgSrc",
        type: "file",
        required: false,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
    ],
  },
  {
    name: "isLoyaltyReferralCodeExtendedEnabled",
    type: "boolean",
    friendlyName: "Enable MGM Referral code on account dashboard",
  },
  {
    name: "referredByFriendConfiguration",
    type: "object",
    showIf: `options.get("isLoyaltyReferralCodeExtendedEnabled") === true`,
    defaultValue: {
      heading: "Have a referral code?",
      label: "Get [Reward] when making your first online purchase.",
      list: [{ value: "Enter the referral code." }, { value: "Receive [Reward]." }],
      legalText: "*Purchase must include a Ploom device",
      discountImageDesktop: null,
      discountImageTablet: null,
      discountImageMobile: null,
      discountHeading: "20%",
      discountLabel: "OFF First order",
      discountSubText: "Apply Referral Code Now!",
      successImageDesktop: null,
      successImageMobile: null,
      successHeading: "Referral Code Applied!",
      successLabel: "You're all set to receive your reward. Make your first purchase to enjoy [REWARD DESCRIPTION].",
      successBtnLabel: "Shop now",
      successLink: "/en/shop",
      inputLabel: "Referral code",
      inputPlaceholder: "Enter code",
      inputHelperText: "e.g ABCD123",
      btnText: "Apply",
      validationRequiredMessage: "This field is required",
      verifyErrorMessage: "Invalid code. Please try again.",
      isHidden: true,
    },
    subFields: loyaltyReferralCodeExtendedInputs,
  },
  {
    name: "orderBannerConfiguration",
    type: "object",
    defaultValue: {
      bannerBackgroundImage: "",
    },
    subFields: [
      {
        name: "bannerBackgroundImage",
        type: "file",
        friendlyName: "Order banner image",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: false,
      },
      {
        name: "bannerAltText",
        type: "string",
        friendlyName: "Order banner image alt text",
        helperText: "Alternative text for the banner image. Leave empty for decorative images.",
        required: false,
      },
    ],
  },
] as const satisfies Inputs;

export const UserProfileComponent: RegisteredComponent = {
  name: "UserProfile",
  component: UserProfile,
  inputs: userProfileInputs,
};
